import { useTheme } from '@/context/ThemeContext';
import { customerInterestData } from '@/mocks/fizboData';

interface MusteriIlgisiProps {
  propertyType: string;
  transactionType: string;
  price?: number;
  sqm?: number;
}

export default function MusteriIlgisi({ propertyType, transactionType, price, sqm }: MusteriIlgisiProps) {
  const { isDark } = useTheme();

  const topSegment = customerInterestData[0];

  return (
    <div className="space-y-4">
      {/* Top Match Banner */}
      <div className={`rounded-xl p-4 border-l-4 border-primary-500 ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
            <i className="ri-user-star-line text-primary-500 text-lg" />
          </div>
          <div>
            <p className={`text-xs font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
              En Yüksek Eşleşme
            </p>
            <p className="text-base font-bold text-primary-500 mt-0.5">{topSegment.segment}</p>
            <p className={`text-xs mt-1 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
              Bu ilan {topSegment.segment.toLowerCase()} segmentine %{topSegment.match} oranında hitap ediyor.
              Portföyünüzde bu segmentten <span className="font-semibold text-primary-500">{topSegment.count} potansiyel alıcı</span> var.
            </p>
          </div>
        </div>
      </div>

      {/* Segment Cards */}
      <div>
        <p className={`text-xs font-semibold mb-3 uppercase tracking-wide ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
          Segment Eşleşme Analizi
        </p>
        <div className="space-y-2.5">
          {customerInterestData.map((seg, idx) => (
            <div
              key={seg.segment}
              className={`rounded-xl p-3.5 ${seg.bg} ${idx === 0 ? 'ring-2 ring-primary-500/30' : ''}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${seg.icon} text-sm ${seg.color}`} />
                  </div>
                  <span className={`text-sm font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                    {seg.segment}
                  </span>
                  {idx === 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-primary-500 text-white font-medium">
                      En İyi
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                    {seg.count} alıcı
                  </span>
                  <span className={`text-sm font-bold ${seg.color}`}>%{seg.match}</span>
                </div>
              </div>
              <div className={`w-full h-2 rounded-full ${isDark ? 'bg-dark-border/50' : 'bg-white/60'}`}>
                <div
                  className={`h-full rounded-full transition-all duration-700 ${
                    seg.match >= 85 ? 'bg-green-500' : seg.match >= 70 ? 'bg-sky-500' : seg.match >= 55 ? 'bg-amber-500' : 'bg-gray-400'
                  }`}
                  style={{ width: `${seg.match}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Property Summary */}
      <div className={`rounded-xl p-4 ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
        <p className={`text-xs font-semibold mb-3 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
          İlan Özellikleri Analizi
        </p>
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: 'Mülk Türü', value: propertyType, icon: 'ri-home-line' },
            { label: 'İşlem', value: transactionType, icon: 'ri-exchange-line' },
            ...(price ? [{ label: 'Fiyat', value: `${(price / 1000000).toFixed(2)}M ₺`, icon: 'ri-price-tag-3-line' }] : []),
            ...(sqm ? [{ label: 'Alan', value: `${sqm} m²`, icon: 'ri-ruler-line' }] : []),
          ].map((item) => (
            <div key={item.label} className={`flex items-center gap-2 p-2.5 rounded-lg ${isDark ? 'bg-dark-card' : 'bg-white'}`}>
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-xs text-primary-500`} />
              </div>
              <div>
                <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>{item.label}</p>
                <p className={`text-xs font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>{item.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Action Suggestion */}
      <div className={`rounded-xl p-4 ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
        <p className={`text-xs font-semibold mb-2 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
          Önerilen Aksiyon
        </p>
        <ul className="space-y-2">
          {[
            { icon: 'ri-send-plane-line', text: `${topSegment.count} ${topSegment.segment.toLowerCase()} müşterinize bu ilanı hemen gönderin.` },
            { icon: 'ri-message-3-line', text: 'Toplu mesaj ile segmente özel sunum hazırlayın.' },
            { icon: 'ri-calendar-line', text: 'Bu hafta içinde en az 3 müşteri ile görüşme planlayın.' },
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className={`${item.icon} text-xs text-primary-500`} />
              </div>
              <span className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>{item.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
